/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable max-lines */
import { Box, Typography } from '@mui/material'
import {
  ComposedChart,
  Legend,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  Cell,
  ReferenceLine,
} from 'recharts'
import { useI18n, useSize } from '@popety_io/popety-io-lib'
import chroma from 'chroma-js'
import { useMemo } from 'react'
import EnergyHeatConsumptionDesc from './EnergyHeatConsumptionDesc'

const CustomizedLegend = ({ current }: { current?: string }) => {
  const { t } = useI18n()

  return (
    <Box
      display="flex"
      flexDirection="row"
      justifyContent="center"
      alignItems="center"
      gap={1}
    >
      {current && (
        <>
          <Box
            sx={{
              width: '10px',
              height: '10px',
              backgroundColor: '#00ade6',
            }}
          />
          <Typography variant="body2">{t(`common.currentIDC`)}</Typography>
        </>
      )}
      <Box
        sx={{
          width: '10px',
          height: '10px',
          backgroundColor: '#E63900',
        }}
      />
      <Typography variant="body2">
        {t(`common.Réno énérgé obligatoire 2022`)}
      </Typography>
      <Box
        sx={{
          width: '10px',
          height: '10px',
          backgroundColor: '#FC8723',
        }}
      />
      <Typography variant="body2">
        {t(`common.Réno énérgé obligatoire 2027`)}
      </Typography>
      <Box
        sx={{
          width: '10px',
          height: '10px',
          backgroundColor: '#FFCB60',
        }}
      />
      <Typography variant="body2">
        {t(`common.Réno énérgé obligatoire 2031`)}
      </Typography>
    </Box>
  )
}

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null

  const { t } = useI18n()

  return (
    <Box
      sx={{
        padding: '10px',
        backgroundColor: '#fff',
        border: '1px solid #ccc',
      }}
    >
      <Typography variant="body1">{`${t('common.Indice de chaleur')} : ${
        payload[0].payload.key
      }`}</Typography>
      <Typography variant="body1">{`${t('common.buildingNumber')} : ${
        payload[0].value
      }`}</Typography>
      {payload?.[0]?.payload?.currentHeatConsumption && (
        <Typography variant="body1" color="#00ade6">{`${t(
          'common.currentIDC',
        )} : ${payload[0].payload.currentHeatConsumption}`}</Typography>
      )}
      {payload?.[0]?.payload?.renovationIndice && (
        <Typography variant="body1" color={payload?.[0]?.payload?.color}>
          {`${t('common.Réno énérgé obligatoire')} : ${
            payload[0].payload.renovationIndice
          }`}
        </Typography>
      )}
    </Box>
  )
}

const EnergyHeatConsumptionAround = ({ data, heatConsumptionIndex }: any) => {
  const { t } = useI18n()
  const { ref, width = 300 } = useSize()

  const currentHeatConsumptionIndex = useMemo(
    () => data?.find((entry: any) => entry?.currentHeatConsumption)?.from,
    [data, heatConsumptionIndex],
  )

  const chromaScale = (value: number) => {
    const colorScale = chroma
      .scale([
        '#82C066',
        '#93C669',
        '#AACF6D',
        '#BDD670',
        '#DBDE75',
        '#F8E97B',
        '#F6DD77',
        '#F2D273',
        '#F1C96F',
        '#EEBF6C',
        '#EAB267',
        '#E7A363',
        '#E4945D',
        '#E2895A',
        '#DF8058',
        '#DF7955',
        '#DD6951',
        '#DB5E4E',
        '#BA403D',
        '#B32926',
      ])
      .mode('lch')
      .colors(data?.length)

    return colorScale[value]
  }

  const xAxisTicks = [0, 200, 400, 600, 800, 950]

  return (
    <Box paddingY={2} display="flex" flexDirection="column" gap={2}>
      <Typography
        variant="h6"
        sx={{
          marginBottom: '4px',
          fontSize: '1.1rem',
          textTransform: 'uppercase',
        }}
      >
        {t('common.Indice de chaleur')} (IDC)
      </Typography>
      {heatConsumptionIndex && (
        <EnergyHeatConsumptionDesc current={heatConsumptionIndex} />
      )}

      <Box ref={ref} sx={{ width: '100%', flexGrow: 1 }}>
        <ComposedChart
          width={width - 20}
          height={300}
          data={data}
          barCategoryGap={0}
          barGap={0}
        >
          <XAxis
            dataKey="from"
            ticks={xAxisTicks}
            tickLine={false}
            interval={0}
            tickFormatter={(tick: any) => (tick === 950 ? '950-*' : tick)}
            textAnchor="end"
          />
          <YAxis
            label={{
              value: t('common.buildingNumber'),
              angle: -90,
              position: 'insideLeft',
            }}
          />
          <Tooltip content={CustomTooltip} />
          <Bar dataKey="doc_count">
            {data?.map((entry: any, index: number) => (
              <Cell key={`cell-${index}`} fill={chromaScale(index)} />
            ))}
          </Bar>
          {currentHeatConsumptionIndex && (
            <>
              <ReferenceLine
                x={currentHeatConsumptionIndex}
                stroke="#00ade6"
                strokeWidth={1}
                isFront
              />
            </>
          )}
          <ReferenceLine x={550} stroke="#FFCB60" strokeWidth={1} isFront />
          <ReferenceLine x={650} stroke="#FC8723" strokeWidth={1} isFront />
          <ReferenceLine x={800} stroke="#E63900" strokeWidth={1} isFront />
          <Legend
            content={<CustomizedLegend current={currentHeatConsumptionIndex} />}
          />
        </ComposedChart>
      </Box>
    </Box>
  )
}

export default EnergyHeatConsumptionAround
